import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EventIcon from "@material-ui/icons/Event";
import TimerIcon from "@material-ui/icons/Timer";
import GradeIcon from "@material-ui/icons/Grade";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SaveIcon from "@material-ui/icons/Save";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ListAltIcon from "@material-ui/icons/ListAlt";
import managerTestApi from "api/managerTest";
import clsx from "clsx";
import ExamQuestionCard from "components/ExamQuestionCard";
import { ResponseStatus } from "models/common";
import { AnswerContent, ListQuestion, Question } from "models/managerTest";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { timeConvert } from "utils/timeConvert";
import { isLimitedDisplay, pickDisplayedQuestions } from "utils/examPreview";

export interface DetailExamProps {
  valueForm: Question | undefined;
  listQuestion: ListQuestion | any;
  setShowDetailExam: (e: any) => void;
  valueInputShuffle?: number | null;
}

export default function DetailExam({
  listQuestion,
  valueForm,
  setShowDetailExam,
  valueInputShuffle,
}: DetailExamProps) {
  const useStyles = makeStyles((theme) => ({
    wrapper: {
      width: "100%",
      minHeight: "calc(100vh - 200px)",
      display: "flex",
      flexDirection: "column",
    },
    metaCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      padding: "24px 28px",
      marginBottom: 20,
      border: "1px solid #E2E8F0",
      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
    },
    metaTitle: {
      fontSize: "1.25rem",
      fontWeight: 800,
      color: "#0F172A",
      marginBottom: 16,
      lineHeight: 1.4,
    },
    statBadge: {
      backgroundColor: "#F8FAFC",
      border: "1px solid #E2E8F0",
      borderRadius: 12,
      padding: "12px 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    statIcon: {
      color: "#2563EB",
      fontSize: 22,
    },
    statValue: {
      fontSize: "0.95rem",
      fontWeight: 700,
      color: "#0F172A",
    },
    statLabel: {
      fontSize: "0.75rem",
      fontWeight: 600,
      color: "#64748B",
    },
    bannerPreview: {
      backgroundColor: "#EFF6FF",
      borderRadius: 16,
      border: "1px solid #BFDBFE",
      padding: "14px 20px",
      marginBottom: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
    },
    mobileFloatingTopNav: {
      display: "none",
      [theme.breakpoints.down("sm")]: {
        display: "block",
        position: "sticky",
        top: 70,
        zIndex: 95,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(12px)",
        borderRadius: 16,
        border: "1px solid #CBD5E1",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.1)",
        padding: "10px 14px",
        marginTop: 16,
        marginBottom: 16,
        transition: "border-radius 0.2s ease, box-shadow 0.2s ease",
      },
    },
    mobileFloatingTopNavSticky: {
      [theme.breakpoints.down("sm")]: {
        borderRadius: "0 0 16px 16px !important",
        borderTop: "none !important",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12) !important",
      },
    },
    mobileBadgeScrollContainer: {
      display: "flex",
      alignItems: "center",
      overflowX: "auto",
      gap: 8,
      padding: "10px 6px",
      minHeight: 56,
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    sidebarCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 16,
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
      padding: "20px",
      position: "sticky",
      top: 90,
      maxHeight: "calc(100vh - 120px)",
      overflowY: "auto",
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    sidebarTitle: {
      fontWeight: 700,
      color: "#0F172A",
      fontSize: "0.95rem",
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 14,
    },
    questionGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
      gap: 8,
      marginTop: 12,
      marginBottom: 16,
      padding: "6px 4px",
    },
    questionBadge: {
      minWidth: 40,
      height: 40,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      fontSize: 13,
      cursor: "pointer",
      transition: "all 0.2s ease-in-out",
      userSelect: "none",
      flexShrink: 0,
      boxSizing: "border-box",
      "&:hover": {
        transform: "translateY(-2px)",
      },
    },
    questionBadgeValid: {
      backgroundColor: "#ECFDF5",
      color: "#065F46",
      border: "1px solid #A7F3D0",
    },
    questionBadgeInvalid: {
      backgroundColor: "#FEF3C7",
      color: "#92400E",
      border: "1px solid #FDE68A",
    },
    questionBadgeActive: {
      boxShadow: "0 0 0 2px #2563EB, 0 4px 12px rgba(37, 99, 235, 0.35)",
      backgroundColor: "#2563EB !important",
      color: "#FFFFFF !important",
      border: "1px solid #1D4ED8 !important",
      transform: "translateY(-2px)",
      fontWeight: 800,
      zIndex: 2,
    },
    stickyFooter: {
      position: "sticky",
      bottom: 16,
      zIndex: 100,
      backgroundColor: "rgba(255, 255, 255, 0.94)",
      backdropFilter: "blur(12px)",
      borderRadius: 16,
      padding: "14px 24px",
      border: "1px solid #E2E8F0",
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.1)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "auto",
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
      color: "#FFFFFF",
      borderRadius: 12,
      padding: "10px 28px",
      fontWeight: 700,
      fontSize: "0.9rem",
      textTransform: "none",
      boxShadow: "0 4px 14px rgba(16, 185, 129, 0.3)",
      "&:hover": {
        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
      },
    },
    btnSecondary: {
      backgroundColor: "#F1F5F9",
      color: "#475569",
      borderRadius: 12,
      padding: "10px 20px",
      fontWeight: 600,
      fontSize: "0.9rem",
      textTransform: "none",
      border: "1px solid #CBD5E1",
      "&:hover": {
        backgroundColor: "#E2E8F0",
        color: "#0F172A",
      },
    },
  }));

  const { t } = useTranslation();
  const params = useParams();
  const classes = useStyles();
  const navigate = useNavigate();

  const [previewAsStudent, setPreviewAsStudent] = useState(true);
  const [shuffleKey, setShuffleKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(0);
  const [isStickyMobile, setIsStickyMobile] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // AGENTS.md parent overflow fix
  useEffect(() => {
    const targetParents: HTMLElement[] = [];
    const originalOverflows: string[] = [];

    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent && parent.tagName !== "MAIN" && parent.tagName !== "BODY") {
        const computedStyle = window.getComputedStyle(parent);
        if (
          computedStyle.overflow === "hidden" ||
          computedStyle.overflowX === "hidden" ||
          computedStyle.overflowY === "hidden"
        ) {
          targetParents.push(parent);
          originalOverflows.push(parent.style.overflow);
          parent.style.overflow = "visible";
        }
        parent = parent.parentElement;
      }
    }

    return () => {
      targetParents.forEach((parent, index) => {
        if (parent) {
          parent.style.overflow = originalOverflows[index];
        }
      });
    };
  }, []);

  const displayedQuestions = React.useMemo(() => {
    if (!previewAsStudent) {
      return listQuestion;
    }
    return pickDisplayedQuestions(listQuestion, valueInputShuffle, shuffleKey);
  }, [listQuestion, valueInputShuffle, previewAsStudent, shuffleKey]);

  const showPreviewBanner = isLimitedDisplay(valueInputShuffle ?? null, listQuestion?.length ?? 0);

  // IntersectionObserver to track active question
  useEffect(() => {
    if (!displayedQuestions || displayedQuestions.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            const index = parseInt(id.replace("question-", ""), 10) - 1;
            if (!isNaN(index)) {
              setActiveQuestionIndex(index);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: "-20% 0px -50% 0px",
        threshold: 0.1,
      }
    );

    displayedQuestions.forEach((_: any, idx: number) => {
      const el = document.getElementById(`question-${idx + 1}`);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [displayedQuestions]);

  // Horizontal scroll mobile badge container
  useEffect(() => {
    const container = document.getElementById("mobile-detail-badge-container");
    const badge = document.getElementById(`mobile-detail-badge-${activeQuestionIndex}`);
    if (container && badge) {
      const containerRect = container.getBoundingClientRect();
      const badgeRect = badge.getBoundingClientRect();
      const scrollLeftTarget =
        container.scrollLeft +
        (badgeRect.left - containerRect.left) -
        containerRect.width / 2 +
        badgeRect.width / 2;
      container.scrollTo({ left: Math.max(0, scrollLeftTarget), behavior: "smooth" });
    }
  }, [activeQuestionIndex]);

  // Sentinel IntersectionObserver for sticky mobile nav
  useEffect(() => {
    const sentinel = document.getElementById("sticky-detail-sentinel");
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStickyMobile(!entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: "-72px 0px 0px 0px",
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToQuestion = (idx: number) => {
    const element = document.getElementById(`question-${idx + 1}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleSendForm = async () => {
    setIsSubmitting(true);
    const value = {
      title: valueForm?.title,
      questions: listQuestion.map((question: ListQuestion) => ({
        answers: question.answers.map((answer: AnswerContent) => ({
          answerContent: answer.answerContent ?? (answer as any).content,
          isRightAnswer: Boolean(answer.isRightAnswer),
        })),
        point: question.point === "" ? null : Number(question.point),
        questionContent: question.questionContent,
        imageFileIDList: (question.imageFileIDList || []).map((x: any) => x?.imageFile?.id ?? x?.id),
      })),
      examTime: valueForm?.examTime,
      prizeId: valueForm?.prizeId,
      startDate: valueForm?.startDate,
      totalPoint: Number(valueForm?.totalPoint) || 0,
      type: valueForm?.type,
      departmentId: valueForm?.departmentId,
      classId: valueForm?.classId,
      classIdList: valueForm?.classIdList || valueForm?.classId,
      userId: valueForm?.userId,
      userIdList: valueForm?.userIdList || valueForm?.userId,
      publishAnswerDate: valueForm?.publishAnswerDate,
      numberOfDisplayedQuestion:
        valueInputShuffle != null && valueInputShuffle > 0 ? valueInputShuffle : null,
    };
    try {
      const res: ResponseStatus = await managerTestApi.updateExam(value, params.id);
      if (res.responseCode === "00") {
        toast.success(`${t("managerTest.capnhatdethithanhcong")}`);
        navigate("/admin/manager-test/list");
      } else {
        toast.error(`${t("managerTest.capnhatdethithatbai")}`);
      }
    } catch (error) {
      toast.error("Có lỗi xảy ra khi gửi thông tin đề thi");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={containerRef} className={classes.wrapper}>
      {/* Exam Summary Header Card */}
      <Paper className={classes.metaCard} elevation={0}>
        <Typography className={classes.metaTitle}>
          {valueForm?.title}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={6} sm={4} md={3}>
            <div className={classes.statBadge}>
              <EventIcon className={classes.statIcon} />
              <div>
                <div className={classes.statValue}>
                  {valueForm?.startDate?.slice(0, 10)} ({valueForm?.time})
                </div>
                <div className={classes.statLabel}>Lịch thi</div>
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <div className={classes.statBadge}>
              <TimerIcon className={classes.statIcon} style={{ color: "#7C3AED" }} />
              <div>
                <div className={classes.statValue}>
                  {timeConvert(Number(valueForm?.examTime))}
                </div>
                <div className={classes.statLabel}>Thời gian làm bài</div>
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <div className={classes.statBadge}>
              <GradeIcon className={classes.statIcon} style={{ color: "#F59E0B" }} />
              <div>
                <div className={classes.statValue}>
                  {valueForm?.totalPoint} điểm
                </div>
                <div className={classes.statLabel}>Thang điểm đề thi</div>
              </div>
            </div>
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <div className={classes.statBadge}>
              <VisibilityIcon className={classes.statIcon} style={{ color: "#10B981" }} />
              <div>
                <div className={classes.statValue}>
                  {listQuestion?.length || 0} câu
                </div>
                <div className={classes.statLabel}>Ngân hàng câu hỏi</div>
              </div>
            </div>
          </Grid>
        </Grid>
      </Paper>

      {/* Student View Banner */}
      {showPreviewBanner && (
        <Box className={classes.bannerPreview}>
          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
            <Chip
              label="Chế độ xem thử"
              size="small"
              style={{ backgroundColor: "#2563EB", color: "#FFFFFF", fontWeight: 700 }}
            />
            <Typography variant="body2" style={{ fontWeight: 600, color: "#1E40AF" }}>
              {previewAsStudent
                ? `Hiển thị ${displayedQuestions.length} / ${listQuestion.length} câu ngẫu nhiên cho học viên`
                : `Hiển thị toàn bộ ${listQuestion.length} câu trong ngân hàng đề`}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" style={{ gap: 10 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPreviewAsStudent((v) => !v)}
              style={{
                borderRadius: 8,
                fontWeight: 600,
                fontSize: "0.8rem",
                borderColor: "#3B82F6",
                color: "#1D4ED8",
                textTransform: "none",
              }}
            >
              {previewAsStudent ? "Xem tất cả câu" : "Chỉ xem N câu ngẫu nhiên"}
            </Button>
            {previewAsStudent && (
              <Button
                size="small"
                variant="contained"
                startIcon={<RefreshIcon style={{ fontSize: 16 }} />}
                onClick={() => setShuffleKey((k) => k + 1)}
                style={{
                  backgroundColor: "#2563EB",
                  color: "#FFFFFF",
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  textTransform: "none",
                }}
              >
                Shuffle lại
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Mobile Floating Top Navigation */}
      <div id="sticky-detail-sentinel" style={{ height: 1, marginTop: -1 }} />
      <Box
        id="mobile-detail-floating-top-nav"
        className={clsx(
          classes.mobileFloatingTopNav,
          isStickyMobile && classes.mobileFloatingTopNavSticky
        )}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
            Danh sách câu hỏi ({displayedQuestions?.length || 0})
          </Typography>
          <Typography style={{ fontSize: 12, fontWeight: 600, color: "#64748B" }}>
            Chạm để chuyển câu
          </Typography>
        </Box>
        <div id="mobile-detail-badge-container" className={classes.mobileBadgeScrollContainer}>
          {displayedQuestions?.map((_: any, idx: number) => {
            const isActive = activeQuestionIndex === idx;
            return (
              <div
                key={idx}
                id={`mobile-detail-badge-${idx}`}
                className={clsx(
                  classes.questionBadge,
                  classes.questionBadgeValid,
                  isActive && classes.questionBadgeActive
                )}
                onClick={() => {
                  setActiveQuestionIndex(idx);
                  scrollToQuestion(idx);
                }}
              >
                {idx + 1}
              </div>
            );
          })}
        </div>
      </Box>

      {/* Grid Layout: Left Question Navigation Grid Sidebar (Desktop) + Right Displayed Questions List */}
      <Grid container spacing={3}>
        {/* Left Column (Desktop): Question Navigation Grid Sidebar */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper className={classes.sidebarCard} elevation={0}>
            <div className={classes.sidebarTitle}>
              <ListAltIcon style={{ color: "#2563EB" }} />
              <span>Danh sách câu hỏi</span>
            </div>

            <div className={classes.questionGrid}>
              {displayedQuestions?.map((_: any, idx: number) => {
                const isActive = activeQuestionIndex === idx;
                return (
                  <div
                    key={idx}
                    className={clsx(
                      classes.questionBadge,
                      classes.questionBadgeValid,
                      isActive && classes.questionBadgeActive
                    )}
                    onClick={() => {
                      setActiveQuestionIndex(idx);
                      scrollToQuestion(idx);
                    }}
                  >
                    {idx + 1}
                  </div>
                );
              })}
            </div>
          </Paper>
        </Grid>

        {/* Right Column (Desktop): Displayed Questions List */}
        <Grid item xs={12} md={8} lg={9}>
          {displayedQuestions?.map((item: ListQuestion, index: number) => (
            <ExamQuestionCard
              key={index}
              question={item}
              index={index}
              mode="view"
              isEssay={valueForm?.category === "essay"}
            />
          ))}
        </Grid>
      </Grid>

      {/* Sticky Bottom Actions */}
      <Box className={classes.stickyFooter}>
        <Button
          className={classes.btnSecondary}
          startIcon={<ArrowBackIcon />}
          onClick={() => setShowDetailExam(false)}
        >
          Quay lại
        </Button>

        <Button
          className={classes.btnPrimary}
          startIcon={<SaveIcon />}
          onClick={handleSendForm}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Đang lưu..." : "Cập nhật đề thi ✓"}
        </Button>
      </Box>
    </div>
  );
}



